export const PEER_REVIEW_COLUMN_KEYS = [
  'patientName',
  'uhid',
  'age',
  'gender',
  'studyDateTime',
  'modality',
  'studyDescription',
  'bodyPart',
  'referringPhysician',
  'clinicalHistory',
  'priority',
  'assignedRadiologist',
  'reportStatus',
] as const;

export type PeerReviewColumnKey = (typeof PEER_REVIEW_COLUMN_KEYS)[number];

export const PEER_REVIEW_COLUMN_LABELS: Record<PeerReviewColumnKey, string> = {
  patientName: 'Patient Name',
  uhid: 'UHID',
  age: 'Age',
  gender: 'Gender',
  studyDateTime: 'Study Date & Time',
  modality: 'Modality',
  studyDescription: 'Study Description',
  bodyPart: 'Body Part',
  referringPhysician: 'Referring Physician',
  clinicalHistory: 'Clinical History',
  priority: 'Priority',
  assignedRadiologist: 'Assigned Radiologist',
  reportStatus: 'Report Status',
};

export const PEER_REVIEW_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

export const PEER_REVIEW_MODALITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'XR', label: 'X-ray' },
  { value: 'US', label: 'Ultrasound' },
  { value: 'MR', label: 'MRI' },
  { value: 'CT', label: 'CT' },
];

/** Bump when default column set changes so stale localStorage does not hide new columns. */
export const PEER_REVIEW_COLUMN_STORAGE_KEY = 'peerReview.columnVisibility.v3';

export function getDefaultPeerReviewColumns(): Record<PeerReviewColumnKey, boolean> {
  return {
    patientName: true,
    uhid: true,
    age: true,
    gender: true,
    studyDateTime: true,
    modality: true,
    studyDescription: true,
    bodyPart: true,
    referringPhysician: true,
    clinicalHistory: true,
    priority: true,
    assignedRadiologist: true,
    reportStatus: true,
  };
}

export function loadPeerReviewColumnVisibility(): Record<PeerReviewColumnKey, boolean> {
  const defaults = getDefaultPeerReviewColumns();
  try {
    const raw = localStorage.getItem(PEER_REVIEW_COLUMN_STORAGE_KEY);
    if (!raw) {
      return defaults;
    }
    const parsed = JSON.parse(raw) as Partial<Record<PeerReviewColumnKey, boolean>>;
    return { ...defaults, ...parsed };
  } catch {
    return defaults;
  }
}

export function persistPeerReviewColumnVisibility(
  visibility: Record<PeerReviewColumnKey, boolean>
): void {
  try {
    localStorage.setItem(PEER_REVIEW_COLUMN_STORAGE_KEY, JSON.stringify(visibility));
  } catch {
    /* ignore quota / private mode */
  }
}
